import debounce from 'lodash/debounce';
import type {
  PaginatedPatientSessionSummaryList,
  PatientSessionSummary,
  PatientSessionPartialUpdateRequest,
  RecordingUploadRequest,
  DocumentUploadRequest,
  PatientSessionCreateRequest,
  SummarizationReportCreateRequest,
  SummarizationReportMessageUpdate,
  SummarizationReportMessageCreate,
  PhotoUploadRequest,
  ExternalPatientSessionCreate,
  SummarizationReportMessage,
  SummarizationReport,
  PatientSessionDetails,
  Photo,
  Document,
  SessionBillingCodes,
  SupportedMimeTypes,
  Recording
} from '~/api/generated';
import {
  RecordingSourceEnum,
  SessionTypeEnum,
  documentSupportedFileMimeTypesMap,
  FileTypeEnum
} from '~/utils/constants/session';
import { getQueryParams } from '~/utils/url';

const SESSIONS_PAGE_SIZE = 10;

const getCursor = (url: PaginatedPatientSessionSummaryList['next']) => (url ? getQueryParams(url).cursor : null);

export const useSessionStore = defineStore('session', {
  state: () => ({
    sessions: [] as PatientSessionSummary[],
    nextCursor: null as string | null,
    searchQuery: '',
    isLoadingMore: false,
    listRequestId: 0
  }),
  actions: {
    // Without page_size the backend returns the plain complete list
    async fetchAllSessions(type: SessionTypeEnum, search?: string) {
      const apiStore = useApiStore();

      return (await apiStore.api.patientSessions.patientSessionList({
        type,
        search
      })) as unknown as PatientSessionSummary[];
    },

    async getMobileSessions() {
      this.sessions = await this.fetchAllSessions(SessionTypeEnum.Mobile, this.searchQuery || undefined);
    },

    async getDesktopSessions({ reset = false } = {}) {
      const apiStore = useApiStore();
      // The first load after an app start replaces whatever was restored from the storage
      const replace = reset || !this.listRequestId;
      const requestId = ++this.listRequestId;

      const { results, next } = await apiStore.api.patientSessions.patientSessionList({
        type: SessionTypeEnum.Desktop,
        pageSize: SESSIONS_PAGE_SIZE,
        search: this.searchQuery || undefined
      });

      // A newer list request (search, load more) has been made meanwhile
      if (requestId !== this.listRequestId) return;

      if (replace || !next || this.sessions.length <= SESSIONS_PAGE_SIZE) {
        this.sessions = results;
        this.nextCursor = getCursor(next);
        return;
      }

      // Only the newest page is polled, sessions loaded further down are kept as they are
      const refreshedIds = new Set(results.map(({ id }) => id));
      const oldestRefreshed = new Date(results[results.length - 1].created_at);
      const olderSessions = this.sessions.filter(
        (session) => !refreshedIds.has(session.id) && new Date(session.created_at) < oldestRefreshed
      );

      this.sessions = [...results, ...olderSessions];
    },

    async getSessions({ reset = false } = {}) {
      const { isNativePlatform } = usePlatform();

      if (isNativePlatform) {
        await this.getMobileSessions();
      } else {
        await this.getDesktopSessions({ reset });
      }
    },

    async searchSessions(query: string) {
      this.searchQuery = query;

      await this.getSessions({ reset: true });
    },

    async loadMoreSessions() {
      if (!this.nextCursor || this.isLoadingMore) return;

      const apiStore = useApiStore();
      const requestId = ++this.listRequestId;

      this.isLoadingMore = true;

      try {
        const { results, next } = await apiStore.api.patientSessions.patientSessionList({
          type: SessionTypeEnum.Desktop,
          cursor: this.nextCursor as string,
          pageSize: SESSIONS_PAGE_SIZE,
          search: this.searchQuery || undefined
        });

        if (requestId !== this.listRequestId) return;

        this.sessions.push(...results);
        this.nextCursor = getCursor(next);
      } catch {
        // Stops the scroll trigger from retrying at once; the next refresh restores the cursor
        this.nextCursor = null;
      } finally {
        this.isLoadingMore = false;
      }
    },

    // Debounce the function to avoid multiple calls from different components
    getSessionsDebounced() {
      return debounce(this.getSessions, 500);
    },

    createSession({ requestBody }: { requestBody: PatientSessionCreateRequest }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.patientSessionCreate({
        requestBody
      });
    },

    createExternalSession({ requestBody }: { requestBody: ExternalPatientSessionCreate }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.externalPatientSessionCreate({
        companySlug: requestBody.external_provider,
        requestBody
      });
    },

    getSession({ sessionId }: { sessionId: PatientSessionDetails['id'] }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.patientSessionsRetrieve({ sessionId });
    },

    updateSession({
      sessionId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      requestBody: PatientSessionPartialUpdateRequest;
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.patientSessionPartialUpdate({ sessionId, requestBody });
    },

    async deleteSession({ sessionId }: { sessionId: PatientSessionDetails['id'] }) {
      const apiStore = useApiStore();

      await apiStore.api.patientSessions.patientSessionDelete({ sessionId });

      // The poll only refreshes the newest page, so a session further down has to be removed here
      this.sessions = this.sessions.filter(({ id }) => id !== sessionId);
    },

    async createDocument({
      sessionId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      requestBody: {
        file: Blob;
        original_file_name: DocumentUploadRequest['original_file_name'];
        size: DocumentUploadRequest['size'];
      };
    }) {
      const apiStore = useApiStore();
      const uploadStore = useUploadStore();

      const fileType =
        documentSupportedFileMimeTypesMap[requestBody.file.type as keyof typeof documentSupportedFileMimeTypesMap];
      const isRecording = fileType === FileTypeEnum.Recording;
      const recordingSource = isRecording ? RecordingSourceEnum.External : undefined;

      const { url, file_path: file } = await uploadStore.getSignedUrl({
        requestBody: {
          content_type: requestBody.file.type,
          file_type: fileType,
          recording_source: recordingSource
        }
      });

      await uploadStore.uploadFile({ url, body: requestBody.file });

      if (isRecording) {
        return this.createRecording({
          sessionId,
          requestBody: [
            {
              file,
              started_at: new Date().toISOString(),
              completed_at: new Date().toISOString(),
              original_file_name: requestBody.original_file_name,
              size: requestBody.size
            }
          ]
        });
      }

      return apiStore.api.patientSessions.documentCreate({
        sessionId,
        requestBody: {
          file,
          mime_type: requestBody.file.type as SupportedMimeTypes,
          original_file_name: requestBody.original_file_name,
          size: requestBody.size
        }
      });
    },

    async createAnonymizedDocument({
      sessionId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      requestBody: {
        file: Blob;
        original_file_name: DocumentUploadRequest['original_file_name'];
      };
    }) {
      const apiStore = useApiStore();
      const uploadStore = useUploadStore();

      const body = new FormData();

      // @ts-ignore-next-line
      body.append('ocr_enabled', true);
      body.append('file', requestBody.file, requestBody.original_file_name);

      // Get signed url for anonymization
      const { signed_url: url } = await uploadStore.getAnonymizationSignedUrl();

      if (!url.length) {
        throw new Error('Get signed url failed');
      }

      // Upload the file to the signed url and get the anonymized text
      const { anonymized_text: text }: { anonymized_text: string } = await uploadStore
        .uploadAnonymizationFile({ url, body })
        .then((res) => res.json());

      if (!text.length) {
        throw new Error('Anonymization failed');
      }

      // Create the document with the anonymized text
      return apiStore.api.patientSessions.documentCreateAnonymizedText({
        sessionId,
        requestBody: {
          text,
          original_file_name: requestBody.original_file_name
        }
      });
    },

    deleteDocument({ sessionId, documentId }: { sessionId: PatientSessionDetails['id']; documentId: Document['id'] }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.documentDelete({ documentId, sessionId });
    },

    createPhoto({
      sessionId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      requestBody: PhotoUploadRequest;
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.photoCreate({
        sessionId,
        requestBody
      });
    },

    deletePhoto({ photoId, sessionId }: { sessionId: PatientSessionDetails['id']; photoId: Photo['id'] }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.photoDelete({ sessionId, photoId });
    },

    createRecording({
      sessionId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      requestBody: RecordingUploadRequest[];
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.recordingCreate({ sessionId, requestBody });
    },

    deleteRecording({
      sessionId,
      recordingId
    }: {
      sessionId: PatientSessionDetails['id'];
      recordingId: Recording['id'];
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.recordingDelete({ recordingId, sessionId });
    },

    createReport({
      sessionId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      requestBody: SummarizationReportCreateRequest;
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.summarizationReportCreate({ sessionId, requestBody });
    },

    updateReportSummary({
      sessionId,
      reportId,
      messageId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      reportId: SummarizationReport['id'];
      messageId: SummarizationReportMessage['id'];
      requestBody: SummarizationReportMessageUpdate;
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.summarizationReportMessageUpdate({
        messageId,
        reportId,
        sessionId,
        requestBody
      });
    },

    adjustReportSummary({
      sessionId,
      reportId,
      requestBody
    }: {
      sessionId: PatientSessionDetails['id'];
      reportId: SummarizationReport['id'];
      requestBody: SummarizationReportMessageCreate;
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.summarizationReportMessagePost({
        sessionId,
        reportId,
        requestBody
      });
    },

    getSessionSummaryAsPdf({
      sessionId,
      reportId,
      messageId
    }: {
      sessionId: PatientSessionDetails['id'];
      reportId: SummarizationReport['id'];
      messageId: SummarizationReportMessage['id'];
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.patientSessionGenerateSummaryPdfLink({ messageId, reportId, sessionId });
    },

    getSessionSummaryAsDocx({
      sessionId,
      reportId,
      messageId
    }: {
      sessionId: PatientSessionDetails['id'];
      reportId: SummarizationReport['id'];
      messageId: SummarizationReportMessage['id'];
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.patientSessionGenerateSummaryDocxLink({ messageId, reportId, sessionId });
    },

    getSessionBillingCodes({ sessionId }: { sessionId: PatientSessionDetails['id'] }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.patientSessionBillingCodesRetrieve({ sessionId });
    },

    updateSessionBillingCodes({
      id,
      sessionId,
      requestBody
    }: {
      id: number;
      sessionId: PatientSessionDetails['id'];
      requestBody: SessionBillingCodes;
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.patientSessionBillingCodesUpdate({ id, sessionId, requestBody });
    },

    exportSessionToExternalProvider({
      sessionId,
      reportId,
      messageId
    }: {
      sessionId: PatientSessionDetails['id'];
      reportId: SummarizationReport['id'];
      messageId: SummarizationReportMessage['id'];
    }) {
      const apiStore = useApiStore();

      return apiStore.api.patientSessions.externalPatientSessionReportSend({ sessionId, reportId, messageId });
    }
  },
  persist: {
    storage: persistedState.localStorage,
    pick: ['sessions']
  }
});
